import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import CategoryFilterSection from "@/pages/home/components/CategoryFilterSection";
import TopicCard from "@/components/feature/TopicCard";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";

export default function Topics() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get("category") ?? "all";
  const initialSort = params.get("sort") === "hot" ? "hot" : "latest";
  const [active, setActive] = useState(initialCategory);
  const [sort, setSort] = useState<"latest" | "hot">(initialSort);
  const [keyword, setKeyword] = useState(params.get("q") ?? "");

  useEffect(() => {
    setActive(params.get("category") ?? "all");
    setSort(params.get("sort") === "hot" ? "hot" : "latest");
    setKeyword(params.get("q") ?? "");
  }, [params]);

  const activeCategory = categories.find((c) => c.id === active);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const result = topics.filter((topic) => {
      const matchCategory = active === "all" || topic.categoryId === active;
      const matchKeyword =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.excerpt.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return sort === "hot"
      ? [...result].sort((a, b) => b.hot - a.hot)
      : [...result].sort((a, b) => b.date.localeCompare(a.date));
  }, [active, sort, keyword]);

  const updateParam = (next: { category?: string; sort?: string }) => {
    const merged = new URLSearchParams(params);
    if (next.category) merged.set("category", next.category);
    if (next.sort) merged.set("sort", next.sort);
    setParams(merged, { replace: true });
  };

  usePageMeta({
    title: "深挖专区 - 全部事件深度档案｜看料档案",
    description:
      "深挖专区汇总看料档案的全部深度专题，涵盖商业起底、行业内幕与舆情深挖三大栏目，支持栏目筛选与关键词搜索，帮你快速定位想看的事件内幕与细节梳理。",
    keywords: "看料黑料深挖,深挖专区,事件起底,行业内幕,舆情深挖,看料黑料细节",
    canonicalPath: "/topics",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: "深挖专区",
          url: "https://kanliao-archive.example/topics",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliao-archive.example/" },
            { "@type": "ListItem", position: 2, name: "深挖专区", item: "https://kanliao-archive.example/topics" },
          ],
        },
        {
          "@type": "ItemList",
          itemListElement: list.map((topic, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: topic.title,
            url: `https://kanliao-archive.example/topics/${topic.slug}`,
          })),
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="relative border-b border-background-200 bg-background-100">
          <div className="paper-grain pointer-events-none absolute inset-0" />
          <div className="relative mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">深挖专区</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              深挖专区
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
              这里按时间与热度归档了全部深度专题。你可以先按栏目缩小范围，再用关键词定位具体事件，每篇档案都标注了信息出处等级与修订记录。
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <div className="flex flex-col gap-5 rounded-xl border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-sm">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  placeholder="搜索专题标题、标签或摘要"
                  aria-label="搜索专题"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActive("all");
                    updateParam({ category: "all" });
                  }}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    active === "all"
                      ? "bg-foreground-950 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  全部
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      setActive(category.id);
                      updateParam({ category: category.id });
                    }}
                    className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                      active === category.id
                        ? "bg-foreground-950 text-background-50"
                        : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
                <span className="mx-1 hidden h-5 w-px bg-background-300 md:block" />
                <div className="flex items-center gap-1 rounded-full border border-background-300 px-1 py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setSort("latest");
                      updateParam({ sort: "latest" });
                    }}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                      sort === "latest"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600"
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSort("hot");
                      updateParam({ sort: "hot" });
                    }}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                      sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>
            </div>

            {activeCategory && (
              <p className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
                {activeCategory.name}：{activeCategory.desc}
              </p>
            )}

            <p className="mt-6 text-sm text-foreground-500">
              共找到 <span className="font-semibold text-foreground-950">{list.length}</span> 篇档案
            </p>

            {list.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} />
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center gap-4 rounded-xl border border-dashed border-background-300 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-file-search-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  没有匹配的档案，试试更换关键词或切换到其他栏目。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setActive("all");
                  }}
                  className="whitespace-nowrap rounded-md border border-foreground-950 px-5 py-2 text-sm text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>

        <CategoryFilterSection topics={topics} />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}